import { ID_COLUMN } from '../csv-import';
import { CsvImportConfig, ImportLookups, ImportOption, ImportValues } from '../csv-import-types';
import { genders, nokPersonaAgeRanges, nokStatus } from '../../../defaults/defaults';

/**
 * Nok personas. Mirrors `NokPersonaImporter` in HQ-API, which is the authority: the rules here
 * only let the grid flag problems early. The signature belongs to the website, so a persona
 * names its website and nothing else.
 */

const options = (choices: { id: string, name: string }[]): ImportOption[] =>
    choices.map((choice: { id: string, name: string }): ImportOption => ({ value: choice.id, label: choice.name }));

const EMAIL_PATTERN: RegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const nokPersonaImport: CsvImportConfig = {
    title: 'Nok Personas',
    endpoint: 'hq/nok-import/personas',
    fileName: 'nok-personas',
    columns: [
        ID_COLUMN,
        { key: 'name', label: 'Name', kind: 'text', required: true, maxLength: 100, width: 180 },
        {
            key: 'email', label: 'Email', kind: 'text', required: true, maxLength: 255, width: 240,
            check: (value: string): string | null => (EMAIL_PATTERN.test(value) ? null : 'Must be an email address.'),
            helper: 'The address the persona sends from. Each persona needs its own.',
        },
        { key: 'gender', label: 'Gender', kind: 'choice', required: true, choices: options(genders), width: 110 },
        { key: 'ageRange', label: 'Age Range', kind: 'choice', required: true, choices: options(nokPersonaAgeRanges), width: 120 },
        { key: 'status', label: 'Status', kind: 'choice', required: true, choices: options(nokStatus), width: 110 },
        {
            key: 'nokWebsite', label: 'Nok Website', kind: 'reference', lookup: 'websites', required: true, width: 260,
            helper: 'The website the persona works for. Its email signature is the website\'s.',
        },
        { key: 'jobTitle', label: 'Job Title', kind: 'text', maxLength: 100, width: 180 },
        {
            key: 'phoneNumber', label: 'Phone Number', kind: 'text', maxLength: 100, width: 150,
            helper: 'Added to the persona signature if applicable.',
        },
        { key: 'persona', label: 'Persona', kind: 'longText', required: true, width: 260 },
        { key: 'disableHtmlEmail', label: 'Disable HTML Emails', kind: 'boolean', width: 120 },
        {
            key: 'productionImagePath', label: 'Production Image Path', kind: 'text', maxLength: 255, width: 200,
            helper: 'The image on the Nok site server. Upload the image itself on the edit screen.',
        },
    ],
    lookups: [
        { key: 'websites', resource: 'nok_websites', label: (record) => record.name + ' (' + record.id + ')' },
    ],
    sampleRows: (lookups: ImportLookups): ImportValues[] => {
        const website: string = lookups.websites?.[0]?.value ?? '';

        return [
            {
                name: 'Sam Example',
                email: 'sam.example@example-consulting.co.uk',
                gender: 'male',
                ageRange: '45-54',
                status: 'active',
                nokWebsite: website,
                jobTitle: 'Managing Director',
                phoneNumber: '0113 496 0000',
                persona: 'Sam is the founder, 52, plain spoken and short on time.\nWrites brief emails and expects a quick answer.',
                disableHtmlEmail: 'no',
                productionImagePath: 'assets/team/sam-example.png',
            },
            {
                name: 'Priya Example',
                email: 'priya.example@example-consulting.co.uk',
                gender: 'female',
                ageRange: '25-34',
                status: 'testing',
                nokWebsite: website,
                jobTitle: 'Office Manager',
                phoneNumber: '',
                persona: 'Priya runs the office and handles supplier enquiries. Friendly and thorough.',
                disableHtmlEmail: 'yes',
                productionImagePath: '',
            },
        ];
    },
};
